import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground border border-border",
        y: "bg-y/10 text-y border-y/20 uppercase tracking-wider font-black",
        success: "bg-green-500/10 text-green-500 border-green-500/20",
        warning: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
        error: "bg-red-500/10 text-red-500 border-red-500/20",
        info: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      },
      size: {
        xs: "px-1.5 py-0.5 text-[10px] rounded",
        sm: "px-2 py-0.5 text-xs rounded-md",
        md: "px-2.5 py-0.5 text-xs rounded-full",
        lg: "px-3 py-1 text-sm rounded-full",
      },
      appearance: {
        filled: "",
        outlined: "",
        subtle: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      appearance: "filled",
    },
    compoundVariants: [
      // Outlined style: transparent background, stronger border
      {
        appearance: "outlined",
        variant: "success",
        class: "bg-transparent border-green-500/40",
      },
      {
        appearance: "outlined",
        variant: "warning",
        class: "bg-transparent border-yellow-500/40",
      },
      {
        appearance: "outlined",
        variant: "error",
        class: "bg-transparent border-red-500/40",
      },
      {
        appearance: "outlined",
        variant: "info",
        class: "bg-transparent border-blue-500/40",
      },
      {
        appearance: "outlined",
        variant: "default",
        class: "bg-transparent border-primary/40",
      },
    ],
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}

function Badge({
  className,
  variant,
  size,
  appearance,
  style,
  icon,
  children,
  ...props
}: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size, appearance }), className)}
      {...props}
    >
      {icon && <span className="inline-flex shrink-0 mr-0.5">{icon}</span>}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
